import { memo, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import { useUnwindLogs } from '../../api/useUnwindLogs';
import { useUnwindLogsSocket } from '../../hooks/useUnwindLogsSocket';
import {
  UnwindLogCategory,
  UnwindLogEvent,
  UnwindLogLevel,
} from '../../types/unwind-logs.types';

const LEVEL_COLOR: Record<UnwindLogLevel, string> = {
  debug: '#9aa0a6',
  info: '#1e88e5',
  warn: '#f9a825',
  error: '#e53935',
};

const CATEGORY_COLOR: Record<UnwindLogCategory, string> = {
  ws: '#00897b',
  evaluation: '#3949ab',
  order: '#43a047',
  fill: '#7cb342',
  notification: '#ec407a',
  error: '#e53935',
  state: '#5e35b1',
  recovery: '#00acc1',
  skip: '#757575',
  rule: '#5e35b1',
  heartbeat: '#9e9e9e',
};

const LEVEL_RANK: Record<UnwindLogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

interface RuleLogsPanelProps {
  ruleId: string;
}

/**
 * Per-rule dev-logs panel — slots into the rule detail view (alongside the
 * ExecutionLogTable). Live tail at top via WebSocket, paginated history
 * loaded via `GET /rules/:ruleId/logs` below. Client-side filter by minimum
 * level (debug → error). Click a row to expand its payload.
 */
// Convert a `<input type="datetime-local">` string to epoch ms. Empty
// string → undefined so the filter is treated as "no bound on this side".
const parseLocal = (s: string): number | undefined => {
  if (!s) return undefined;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : undefined;
};

const ALL_CATEGORIES: UnwindLogCategory[] = [
  'ws',
  'evaluation',
  'order',
  'fill',
  'notification',
  'error',
  'state',
  'recovery',
  'skip',
  'rule',
  'heartbeat',
];

/**
 * Hard ceiling on rendered rows. Live tail can stack to MAX_RING (1000) on
 * the socket side plus paginated history; rendering 1000+ MUI rows on every
 * 50ms WS flush is what makes the drawer feel sluggish. We cap the visible
 * tail at this value — older entries stay reachable through "Load more"
 * (which fetches the next history page from the server, not from the live
 * ring). The cap doesn't drop events that match a time-range filter; it
 * just trims the unfiltered tail before render.
 */
const MAX_VISIBLE_ROWS = 500;

interface LogRowProps {
  ev: UnwindLogEvent;
  isOpen: boolean;
  onToggle: (key: string) => void;
}

const LogRow = memo(function LogRow({ ev, isOpen, onToggle }: LogRowProps) {
  const key = ev.id || `${ev.ts}:${ev.event}:${ev.message}`;
  const hasPayload = !!ev.payload && Object.keys(ev.payload).length > 0;
  const ts = new Date(ev.ts);
  const tsLabel = `${ts.toLocaleTimeString()}.${String(ts.getMilliseconds()).padStart(3, '0')}`;
  const catColor = CATEGORY_COLOR[ev.category] ?? '#666';
  const lvlColor = LEVEL_COLOR[ev.level] ?? '#666';

  return (
    <Box
      sx={{
        borderLeft: `3px solid ${catColor}`,
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        py: 0.5,
        pl: 1,
        pr: 1,
        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        fontSize: '0.72rem',
        cursor: hasPayload ? 'pointer' : 'default',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
      }}
      onClick={() => hasPayload && onToggle(key)}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton
          size="small"
          sx={{ p: 0, visibility: hasPayload ? 'visible' : 'hidden' }}
        >
          {isOpen ? (
            <KeyboardArrowDownIcon fontSize="inherit" />
          ) : (
            <KeyboardArrowRightIcon fontSize="inherit" />
          )}
        </IconButton>
        <Typography sx={{ width: 110, color: 'grey.500', fontSize: 'inherit' }}>
          {tsLabel}
        </Typography>
        <Chip
          label={ev.category}
          size="small"
          sx={{
            height: 16,
            fontSize: '0.62rem',
            bgcolor: catColor,
            color: 'white',
            '.MuiChip-label': { px: 0.6 },
          }}
        />
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: lvlColor,
          }}
        />
        {ev.platform && (
          <Typography sx={{ width: 70, color: 'grey.500', fontSize: 'inherit' }}>
            {ev.platform}
          </Typography>
        )}
        <Typography sx={{ width: 180, color: 'grey.400', fontSize: 'inherit' }}>
          {ev.event}
        </Typography>
        <Typography sx={{ flex: 1, color: 'grey.100', fontSize: 'inherit' }}>
          {ev.message}
        </Typography>
        <IconButton
          size="small"
          sx={{ p: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            void navigator.clipboard.writeText(JSON.stringify(ev));
          }}
          title="Copy event JSON"
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Stack>
      {isOpen && hasPayload && (
        <Box
          sx={{
            mt: 0.5,
            ml: 4,
            p: 1,
            bgcolor: 'rgba(0,0,0,0.3)',
            borderRadius: 1,
            color: 'grey.300',
            whiteSpace: 'pre',
            overflowX: 'auto',
          }}
        >
          {JSON.stringify(ev.payload, null, 2)}
        </Box>
      )}
    </Box>
  );
});

export const RuleLogsPanel = ({ ruleId }: RuleLogsPanelProps) => {
  const [liveTail, setLiveTail] = useState(true);
  const [minLevel, setMinLevel] = useState<UnwindLogLevel>('debug');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [fromStr, setFromStr] = useState<string>('');
  const [toStr, setToStr] = useState<string>('');
  const [category, setCategory] = useState<UnwindLogCategory | 'all'>('all');

  const live = useUnwindLogsSocket({ ruleId, enabled: liveTail });
  const history = useUnwindLogs(ruleId, { enabled: !!ruleId });

  const merged = useMemo(() => {
    const seen = new Set<string>();
    const out: UnwindLogEvent[] = [];
    // Live (newest first) takes precedence.
    for (const ev of live.events) {
      const key = ev.id || `${ev.ts}:${ev.event}:${ev.message}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(ev);
    }
    for (const page of history.data?.pages || []) {
      for (const ev of page.events) {
        const key = ev.id || `${ev.ts}:${ev.event}:${ev.message}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(ev);
      }
    }
    return out;
  }, [live.events, history.data]);

  const fromTs = useMemo(() => parseLocal(fromStr), [fromStr]);
  const toTs = useMemo(() => parseLocal(toStr), [toStr]);

  const filtered = useMemo(() => {
    const minRank = LEVEL_RANK[minLevel];
    const out: UnwindLogEvent[] = [];
    for (const e of merged) {
      if (LEVEL_RANK[e.level] < minRank) continue;
      if (category !== 'all' && e.category !== category) continue;
      if (fromTs !== undefined && e.ts < fromTs) continue;
      if (toTs !== undefined && e.ts > toTs) continue;
      out.push(e);
      // Cap on the unfiltered tail. Filtering may drop most entries, in
      // which case we still want to show the matches even if they sit
      // beyond MAX_VISIBLE_ROWS. Only stop early when nothing has been
      // dropped (i.e. we're streaming the natural tail).
      if (
        category === 'all' &&
        minRank === 0 &&
        fromTs === undefined &&
        toTs === undefined &&
        out.length >= MAX_VISIBLE_ROWS
      ) {
        break;
      }
    }
    return out;
  }, [merged, minLevel, category, fromTs, toTs]);

  const toggleExpanded = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const hasTimeFilter = fromTs !== undefined || toTs !== undefined;
  const clearTime = () => {
    setFromStr('');
    setToStr('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 0.5, flexShrink: 0 }}>
        <Typography
          sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'grey.400' }}
        >
          Dev Logs
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.600' }}>
          {filtered.length}/{merged.length}
          {filtered.length >= MAX_VISIBLE_ROWS &&
          category === 'all' &&
          minLevel === 'debug' &&
          fromTs === undefined &&
          toTs === undefined
            ? ` (capped — filter to see more)`
            : ''}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Switch
            size="small"
            checked={liveTail}
            onChange={(e) => setLiveTail(e.target.checked)}
          />
          <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
            Live{' '}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                ml: 0.5,
                bgcolor: live.isConnected ? '#43a047' : '#757575',
              }}
            />
          </Typography>
        </Stack>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <Select
            value={category}
            onChange={(e) =>
              setCategory(e.target.value as UnwindLogCategory | 'all')
            }
            sx={{ fontSize: '0.7rem', height: 24 }}
          >
            <MenuItem value="all">all categories</MenuItem>
            {ALL_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <Select
            value={minLevel}
            onChange={(e) => setMinLevel(e.target.value as UnwindLogLevel)}
            sx={{ fontSize: '0.7rem', height: 24 }}
          >
            <MenuItem value="debug">debug+</MenuItem>
            <MenuItem value="info">info+</MenuItem>
            <MenuItem value="warn">warn+</MenuItem>
            <MenuItem value="error">error</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 0.5, flexShrink: 0 }}
      >
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.500' }}>
          From
        </Typography>
        <TextField
          type="datetime-local"
          size="small"
          value={fromStr}
          onChange={(e) => setFromStr(e.target.value)}
          inputProps={{ step: 1 }}
          sx={{
            '& .MuiInputBase-root': { height: 24, fontSize: '0.7rem' },
            '& input': { color: 'grey.300', py: 0 },
          }}
        />
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.500' }}>
          To
        </Typography>
        <TextField
          type="datetime-local"
          size="small"
          value={toStr}
          onChange={(e) => setToStr(e.target.value)}
          inputProps={{ step: 1 }}
          sx={{
            '& .MuiInputBase-root': { height: 24, fontSize: '0.7rem' },
            '& input': { color: 'grey.300', py: 0 },
          }}
        />
        {hasTimeFilter && (
          <IconButton size="small" onClick={clearTime} title="Clear time range">
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
        <Box sx={{ flex: 1 }} />
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          bgcolor: 'rgba(0,0,0,0.2)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 1,
        }}
      >
        {filtered.length === 0 && (
          <Typography
            sx={{
              p: 2,
              fontSize: '0.72rem',
              color: 'grey.500',
              textAlign: 'center',
            }}
          >
            {history.isLoading
              ? 'Loading…'
              : 'No events yet. Logs appear here as the rule runs.'}
          </Typography>
        )}
        {filtered.map((ev) => {
          const key = ev.id || `${ev.ts}:${ev.event}:${ev.message}`;
          return (
            <LogRow
              key={key}
              ev={ev}
              isOpen={expanded.has(key)}
              onToggle={toggleExpanded}
            />
          );
        })}
      </Box>

      {history.hasNextPage && (
        <Box sx={{ mt: 0.5, textAlign: 'center', flexShrink: 0 }}>
          <Button
            size="small"
            onClick={() => history.fetchNextPage()}
            disabled={history.isFetchingNextPage}
            sx={{ fontSize: '0.7rem' }}
          >
            {history.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
